import { defineComponent, nextTick } from 'vue';

import type { APINewsItem, Response, Facet } from '../shared/types';
import { getCollectionPageStructuredData, getItemListStructuredData, updateStructuredDataScript } from '../shared/structuredData/functions';

// Import general mixin for shared functionality.
import defaultApp from './defaultApp';

// Import components for this specific app.
import NewsItem from './components/NewsItem.vue';
import Swiper from 'swiper';
import swiperConfig from '../vendor/swiper';

export default defineComponent({
    // Overwrite default app functionalities as you please by using the spread operator.
    components: {
        ...defaultApp.components,
        NewsItem
    },
    mixins: [defaultApp],
    data() {
        return {
            data: {
                items: [] as APINewsItem[],
                facets: [] as Facet[],
                queryCount: 0 as number,
            } as Response<APINewsItem>,
            swiperInstance: null as Swiper | null,
        };
    },
    updated() {
        void this.initWidgetSwiper();
    },
    unmounted() {
        this.destroyWidgetSwiper();
    },
    methods: {
        setData(response: any, replace = false): void {
            (defaultApp.methods as any).setData.call(this, response, replace);
            this.updateNewsOverviewStructuredData();
            this.updateNewsWidgetStructuredData();
        },
        destroyWidgetSwiper(): void {
            const swiperInstance = this.swiperInstance as Swiper | null;

            swiperInstance?.destroy(true, true);
            this.swiperInstance = null;
        },
        async initWidgetSwiper(): Promise<void> {
            if (!this.dataWidget || !this.itemsSet) return;

            await nextTick();

            const slider = (this.$el as HTMLElement).querySelector(this.constants.targets.sliders.widgetNews) as HTMLElement | null;
            if (!slider) return;

            this.destroyWidgetSwiper();
            this.swiperInstance = new Swiper(slider, swiperConfig);
        },
        updateNewsOverviewStructuredData(): void {
            if (this.dataWidget || !this.dataStructuredDataScriptId || !this.dataStructuredDataPageName || !this.dataStructuredDataPageUrl) {
                return;
            }

            updateStructuredDataScript(
                this.dataStructuredDataScriptId,
                getCollectionPageStructuredData(
                    this.dataStructuredDataPageName,
                    this.dataStructuredDataPageUrl,
                    this.dataStructuredDataPageDescription,
                    this.data.items
                ),
                this.$el as HTMLElement
            );
        },
        updateNewsWidgetStructuredData(): void {
            if (!this.dataWidget || !this.dataStructuredDataScriptId || !this.dataStructuredDataPageUrl) {
                return;
            }

            updateStructuredDataScript(
                this.dataStructuredDataScriptId,
                getItemListStructuredData(
                    this.dataStructuredDataPageUrl,
                    this.data.items
                ),
                this.$el as HTMLElement
            );
        },
        // Check if a new month should be shown in the template and return the display value if so.
        showMonth(index: number, date: string): string {
            if (!this.orderItems.length) return '';
            let returnVal = '';
            if (index === 0) {
                returnVal = new Intl.DateTimeFormat(this.dataCulture, { month: 'long' }).format(this.toDateObj(date)) + ' ' + this.toDateObj(date).getFullYear();
            }
            else {
                const prevEvent = this.data.items[index - 1];
                if (prevEvent) {
                    if (this.toDateObj(date).getMonth() !== this.toDateObj(prevEvent.date).getMonth() || this.toDateObj(date).getFullYear() !== this.toDateObj(prevEvent.date).getFullYear()) {
                        returnVal = new Intl.DateTimeFormat(this.dataCulture, { month: 'long' }).format(this.toDateObj(date)) + ' ' + this.toDateObj(date).getFullYear();
                    }
                }
            }
            return returnVal;
        },
    }
});

